// src/utils/categoryHelpers.ts

import { useTranslation } from 'react-i18next';
import type { Category, MainCategory, SubCategory } from '~/types/new_types';

/**
 * Get the localized name for a category based on current language
 * @param category - The category object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized category name
 */
export const getCategoryName = (category: Category | MainCategory | SubCategory, language: string): string => {
	if (language === 'ar' && category.ar?.name) {
		return category.ar.name;
	}
	return category.name || '';
};

/**
 * Get the localized description for a category based on current language
 * @param category - The category object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized category description
 */
export const getCategoryDescription = (category: Category | MainCategory | SubCategory, language: string): string | null => {
	if (language === 'ar' && category.ar?.description) {
		return category.ar.description;
	}
	return category.description;
};

/**
 * Hook to get localized category data
 * @param category - The category object
 * @returns Object with localized name and description
 */
export const useLocalizedCategory = (category: Category | MainCategory | SubCategory) => {
	const { i18n } = useTranslation();

	return {
		name: getCategoryName(category, i18n.language),
		description: getCategoryDescription(category, i18n.language)
	};
};